import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import PacotesBox from '@/components/feature/PacotesBox';

const beneficios = [
  {
    icon: 'ri-volume-mute-line',
    titulo: 'Destaque no meio do barulho',
    desc: 'Pavilhões de feira reúnem dezenas de estandes com música, microfones e anúncios ao mesmo tempo. Com fones sem fio, sua demonstração chega limpa ao visitante — sem competir com o estande vizinho e sem incomodá-lo.',
  },
  {
    icon: 'ri-user-voice-line',
    titulo: 'Pitches simultâneos e privados',
    desc: 'Vários vendedores atendem vários visitantes ao mesmo tempo, cada um em seu próprio canal de áudio, sem um pitch atropelar o outro. Ideal para estandes com fluxo alto e equipe comercial numerosa.',
  },
  {
    icon: 'ri-focus-3-line',
    titulo: 'Visitante fica mais tempo no estande',
    desc: 'Sem o barulho do corredor competindo pela atenção, o visitante acompanha a demonstração até o fim — em vez de sair no meio por causa do som do estande vizinho. Mais tempo de estande, mais chance de fechar negócio.',
  },
];

const usos = [
  { icon: 'ri-presentation-line', titulo: 'Demonstração de produto', desc: 'Áudio de demonstração ou vídeo institucional em loop, ouvido só por quem está no estande — sem poluir o corredor.' },
  { icon: 'ri-handshake-line', titulo: 'Pitch comercial individual', desc: 'Cada consultor atende um lead por vez, em canal próprio, sem precisar se afastar do estande para ser ouvido.' },
  { icon: 'ri-team-line', titulo: 'Treinamento de equipe', desc: 'Alinhamento da equipe antes da abertura dos portões, com o pavilhão ainda em montagem ao redor.' },
  { icon: 'ri-headphone-line', titulo: 'Espera ativa para quem aguarda atendimento', desc: 'Enquanto aguardam a vez, visitantes ouvem um vídeo institucional ou cases de sucesso no próprio fone — a fila de espera vira mais um momento de marca, não tempo perdido.' },
  { icon: 'ri-magic-line', titulo: 'Ativação e experiência de marca', desc: 'Silent experience como diferencial visual — visitantes de fone no ouvido chamam atenção de quem passa pelo corredor.' },
  { icon: 'ri-vip-crown-line', titulo: 'Reuniões VIP no estande', desc: 'Conversas com clientes estratégicos ou imprensa em total privacidade, mesmo cercado por outros estandes.' },
];

const etapas = [
  {
    num: '01',
    titulo: 'Levantamento técnico do estande',
    desc: 'Analisamos a metragem, o layout e o número de pessoas esperadas — de um estande de 9m² a uma ilha de grande porte — para dimensionar canais e quantidade de fones.',
  },
  {
    num: '02',
    titulo: 'Instalação de transmissores',
    desc: 'Montagem de transmissores RF discretos dentro do próprio estande, sem depender da estrutura do pavilhão nem de autorização extra do organizador da feira.',
  },
  {
    num: '03',
    titulo: 'Fones para equipe e visitantes',
    desc: 'Fones higienizados, com LED por canal, prontos antes da abertura dos portões — sua equipe já recebe os visitantes com a experiência funcionando.',
  },
  {
    num: '04',
    titulo: 'Suporte técnico presencial',
    desc: 'Técnico STIB disponível durante todos os dias de feira, com fones reserva no estande para qualquer imprevisto.',
  },
  {
    num: '05',
    titulo: 'Desmontagem ao final',
    desc: 'Recolhimento e higienização de todo o equipamento assim que a feira encerra, sem custo extra de logística para a sua equipe.',
  },
];

const venuesFeiraSP = [
  { nome: 'Transamerica Expo Center', bairro: 'Santo Amaro', slug: 'palestra-silenciosa-transamerica-expo' },
  { nome: 'Expo Center Norte', bairro: 'Vila Guilherme', slug: 'palestra-silenciosa-expocenter-norte' },
  { nome: 'Distrito Anhembi', bairro: 'Santana', slug: 'palestra-silenciosa-distrito-anhembi' },
  { nome: 'Centro de Convenções Frei Caneca', bairro: 'Consolação', slug: 'palestra-silenciosa-frei-caneca' },
  { nome: 'WTC Events Center', bairro: 'Brooklin', slug: 'palestra-silenciosa-wtc-events-center' },
  { nome: 'Villa Bisutti', bairro: 'Vila Olímpia', slug: 'palestra-silenciosa-villa-bisutti' },
  { nome: 'Tokio Marine Hall', bairro: 'Várzea de Baixo', slug: 'palestra-silenciosa-tokio-marine-hall' },
];

const modelosFone = [
  {
    img: '/img/stib/fone-led-uhf.webp',
    alt: 'Fone de ouvido sem fio STIB com anel de LED colorido personalizável, modelo UHF',
    badge: 'UHF · LED personalizável',
    titulo: 'Fone Sem Fio LED UHF',
    desc: 'Anel de LED com cor personalizável — pode ser ajustado para combinar com a identidade visual do seu estande. Transmissão UHF de longo alcance, ideal para pavilhões com muitos sinais de rádio ao mesmo tempo. Bateria de 10 a 12 horas de uso contínuo.',
  },
  {
    img: '/img/stib/fone-vhf.jpg',
    alt: 'Fone de ouvido sem fio STIB com visor de canal integrado, modelo VHF',
    badge: 'VHF · Visor de canal',
    titulo: 'Fone Sem Fio VHF',
    desc: 'Modelo compacto com visor de canal integrado, para o visitante confirmar de relance em qual transmissão está conectado. Transmissão VHF estável, a opção mais usada da STIB para uso intensivo durante os dias de feira. Bateria de 10 a 12 horas de uso contínuo.',
  },
];

const perguntas = [
  {
    q: 'Quantos fones preciso para o meu estande?',
    a: 'Depende do tamanho do estande e do fluxo esperado. Estandes pequenos (9 a 18 m²) costumam usar de 10 a 20 fones; ilhas maiores, 50 ou mais. No levantamento técnico dimensionamos junto com você, sem comprometer a circulação do espaço.',
  },
  {
    q: 'Funciona em estandes pequenos, de até 20 m²?',
    a: 'Sim. Os transmissores são compactos e cabem em qualquer estrutura, mesmo em estandes de 9 m². O alcance de rádio frequência cobre o espaço sem precisar de equipamento visível ocupando a área de atendimento.',
  },
  {
    q: 'Como funciona a instalação em pavilhões como Expo Center Norte, Anhembi ou Transamerica Expo?',
    a: 'A instalação é feita dentro do próprio estande, sem depender de infraestrutura do pavilhão. A STIB já operou eventos no Distrito Anhembi — como o Encontro Mundial da Deficiência Visual, em 2025, com 2.000 fones — e planeja montagem e credenciamento conforme as regras de cada pavilhão.',
  },
  {
    q: 'Atende feiras de mais de um dia?',
    a: 'Sim. Cobrimos desde eventos de um único dia até feiras de uma semana, com técnico presencial disponível durante todo o período contratado.',
  },
  {
    q: 'Vocês atendem só São Paulo?',
    a: 'A STIB atende feiras e eventos em todo o Brasil. São Paulo concentra os maiores pavilhões do país — Transamerica Expo, Expo Center Norte e Distrito Anhembi — e é onde temos mais volume de estandes atendidos.',
  },
  {
    q: 'Com quanta antecedência preciso reservar?',
    a: 'Recomendamos 15 dias de antecedência para estandes de até 50 fones. Para feiras grandes, com vários estandes do mesmo cliente, o ideal são 30 dias. Demandas urgentes são atendidas conforme disponibilidade.',
  },
  {
    q: 'Quais modelos de fone a STIB disponibiliza para o estande?',
    a: 'Dois modelos principais: o LED UHF, com anel de LED de cor personalizável — bom para reforçar a identidade visual do estande — e o VHF, com visor de canal integrado, o modelo mais usado para uso intensivo durante os dias de feira. Os dois tem bateria de 10 a 12 horas; o levantamento técnico define qual (ou quais) faz mais sentido para o seu caso.',
  },
];

function BenefitCard({ icon, titulo, desc }: { icon: string; titulo: string; desc: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
      <div className="w-14 h-14 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-6">
        <i className={`${icon} text-2xl text-primary`} aria-hidden="true"></i>
      </div>
      <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-3">{titulo}</h3>
      <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed">{desc}</p>
    </div>
  );
}

function FaqItem({ question, answer, index, isVisible }: { question: string; answer: string; index: number; isVisible: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border-b border-gray-200 dark:border-gray-700 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full py-7 px-6 flex items-center justify-between text-left cursor-pointer group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <h3 className="font-heading font-semibold text-lg text-secondary dark:text-white pr-8 group-hover:text-primary transition-colors">
          {question}
        </h3>
        <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-primary flex-shrink-0 transition-all duration-300`}></i>
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed px-6 pb-7">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function AluguelFonesEstandePage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: beneficiosRef, isVisible: beneficiosVisible } = useScrollAnimation({ threshold: 0.1 });
const { ref: modelosRef, isVisible: modelosVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: usosRef, isVisible: usosVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: etapasRef, isVisible: etapasVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    document.title = 'Aluguel de Fones sem Fio para Estande em Feiras SP | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Aluguel de fones sem fio para estandes em feiras e eventos de São Paulo: demonstrações sem ruído, pitches simultâneos e mais tempo de atenção do visitante. STIB (11) 91777-6227.'
      );
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'aluguel de fones para estande, fones sem fio para feira, locação de fones para estande, silent experience estande, aluguel de fones São Paulo, fones para feira de negócios'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/aluguel-fones-estande-sao-paulo`);

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${siteUrl}/aluguel-fones-estande-sao-paulo#service`,
          name: 'Aluguel de Fones Sem Fio para Estande em Feiras',
          alternateName: ['Locação de Fones para Estande', 'Fones sem Fio para Feira de Negócios', 'Silent Experience para Estande'],
          description:
            'Aluguel de fones de ouvido sem fio para estandes em feiras e eventos de São Paulo. Demonstrações de produto e pitches comerciais sem ruído, canais simultâneos para equipe e visitantes, sem fios expostos no estande. Inclui transmissores, montagem e suporte técnico presencial durante toda a feira.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'STIB — Palestra Silenciosa Brasil',
            telephone: '+55-11-91777-6227',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'São Paulo',
              addressRegion: 'SP',
              addressCountry: 'BR',
            },
          },
          serviceType: 'Locação de Equipamento de Áudio Sem Fio para Estandes',
          areaServed: [
            { '@type': 'City', name: 'São Paulo' },
            { '@type': 'State', name: 'São Paulo' },
          ],
          offers: {
            '@type': 'Offer',
            name: 'Aluguel de Fones para Estande — Orçamento Personalizado',
            description: 'Orçamento personalizado conforme metragem do estande, número de fones e duração da feira. Sem preço fixo publicado — valor definido no levantamento técnico.',
            availability: 'https://schema.org/InStock',
            areaServed: 'BR',
            priceCurrency: 'BRL',
            url: `${siteUrl}/aluguel-fones-estande-sao-paulo`,
            seller: {
              '@type': 'LocalBusiness',
              name: 'STIB — Palestra Silenciosa Brasil',
              telephone: '+55-11-91777-6227',
            },
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: perguntas.map((p) => ({
            '@type': 'Question',
            name: p.q,
            acceptedAnswer: { '@type': 'Answer', text: p.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Locais', item: `${siteUrl}/locais` },
            { '@type': 'ListItem', position: 3, name: 'Aluguel de Fones para Estande', item: `${siteUrl}/aluguel-fones-estande-sao-paulo` },
          ],
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/aluguel-fones-estande-sao-paulo#webpage`,
          url: `${siteUrl}/aluguel-fones-estande-sao-paulo`,
          name: 'Aluguel de Fones sem Fio para Estande em Feiras SP | STIB',
          description: 'Aluguel de fones sem fio para estandes em feiras e eventos de São Paulo. Demonstrações sem ruído, pitches simultâneos e mais tempo de atenção do visitante.',
          inLanguage: 'pt-BR',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/aluguel-fones-estande-sao-paulo#service` },
          dateModified: new Date().toISOString().split('T')[0],
        },
      ],
    };

    const existingSchema = document.getElementById('aluguel-fones-estande-schema');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.id = 'aluguel-fones-estande-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('aluguel-fones-estande-schema');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden pt-20">
          <div className="absolute inset-0">
            <img
              src="/img/stib/local-pavilhao-feira.webp"
              alt="Aluguel de fones sem fio para estande em feiras de São Paulo - STIB"
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/85 via-secondary/65 to-secondary/40"></div>
          </div>
          <div
            ref={heroRef}
            className={`relative max-w-7xl mx-auto px-6 w-full transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <nav className="flex items-center gap-2 text-sm mb-8" aria-label="Breadcrumb">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-white/50" aria-hidden="true"></i>
              <Link to="/locais" className="text-white/70 hover:text-white transition-colors">Locais</Link>
              <i className="ri-arrow-right-s-line text-white/50" aria-hidden="true"></i>
              <span className="text-white font-medium">Aluguel de Fones para Estande</span>
            </nav>
            <div className="max-w-3xl">
              <span className="inline-block bg-primary/20 text-primary border border-primary/30 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wider">
                Feiras e eventos em São Paulo
              </span>
              <h1 className="font-heading font-extrabold text-5xl md:text-7xl text-white mb-6 leading-tight">
                ALUGUEL DE FONES PARA<br />
                <span className="text-primary">ESTANDE</span> EM FEIRAS
              </h1>
              <p className="font-body text-xl text-white/85 leading-relaxed mb-10 max-w-2xl">
                Demonstrações de produto e pitches comerciais sem disputar espaço com o barulho do pavilhão.
                Fones sem fio para o seu estande em feiras de São Paulo, com canais simultâneos para equipe,
                visitantes — montagem e suporte técnico presencial incluídos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contato"
                  className="inline-flex items-center justify-center gap-3 bg-primary text-black px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-message-3-line text-lg" aria-hidden="true"></i>
                  Solicitar Orçamento Grátis
                </Link>
                <a
                  href="https://wa.me/5511917776227?text=Olá! Preciso de orçamento para aluguel de fones para estande em feira."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-green-500 text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-green-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                  (11) 91777-6227
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary dark:bg-gray-800 py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { num: '+5.000', label: 'Fones disponíveis' },
                { num: '+500', label: 'Eventos realizados' },
                { num: 'Até 10', label: 'Canais simultâneos' },
                { num: '500m', label: 'Alcance RF por transmissor' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-heading font-extrabold text-3xl md:text-4xl text-primary mb-1">{s.num}</div>
                  <div className="font-body text-sm text-white/70">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Por que fones sem fio no estande */}
        <section className="py-24 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                POR QUE ALUGAR FONES PARA O SEU ESTANDE
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                O chão de feira é o ambiente mais disputado — e mais barulhento — do calendário de eventos.
                O silêncio bem usado é o que faz o seu estande ser notado.
              </p>
            </div>
            <div ref={beneficiosRef} className="grid md:grid-cols-3 gap-8">
              {beneficios.map((b, i) => (
                <div
                  key={i}
                  className={`transition-all duration-500 ${beneficiosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <BenefitCard icon={b.icon} titulo={b.titulo} desc={b.desc} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Modelos de fone disponiveis */}
<section className="py-24 bg-white dark:bg-gray-900">
  <div className="max-w-6xl mx-auto px-6">
    <div className="text-center mb-16">
      <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
        MODELOS DE FONE DISPONÍVEIS
      </h2>
      <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
        Dois modelos de fone sem fio compõem o kit STIB para estandes — o levantamento técnico define qual combinação faz mais sentido para o seu espaço.
      </p>
    </div>
    <div ref={modelosRef} className="grid sm:grid-cols-2 gap-8">
      {modelosFone.map((m, i) => (
        <div
          key={i}
          className={`bg-gray-50 dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:border-primary/30 hover:shadow-lg transition-all duration-500 ${modelosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: `${i * 100}ms` }}
        >
          <div className="aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-gray-900">
            <img src={m.img} alt={m.alt} className={`w-full h-full ${m.img.includes('vhf') ? 'object-contain object-center bg-white p-4' : 'object-cover'}`} loading="lazy" />
          </div>
          <div className="p-8">
            <span className="inline-block bg-primary/10 dark:bg-primary/20 text-primary text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">{m.badge}</span>
            <h3 className="font-heading font-bold text-2xl text-secondary dark:text-white mb-3">{m.titulo}</h3>
            <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed">{m.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>

{/* Como funciona */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                COMO FUNCIONA NO SEU ESTANDE
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300">Do levantamento técnico à desmontagem, sem tirar o foco da sua equipe da feira</p>
            </div>
            <div ref={etapasRef} className="space-y-6">
              {etapas.map((e, i) => (
                <div
                  key={i}
                  className={`flex gap-6 items-start bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 md:p-8 transition-all duration-500 ${etapasVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <span className="font-heading font-extrabold text-3xl text-primary/40 flex-shrink-0">{e.num}</span>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-2">{e.titulo}</h3>
                    <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed">{e.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Usos mais comuns */}
        <section className="py-24 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                USOS MAIS COMUNS NO ESTANDE
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300">Um mesmo kit de fones, vários jeitos de usar durante a feira</p>
            </div>
            <div ref={usosRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {usos.map((u, i) => (
                <div
                  key={i}
                  className={`bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 transition-all duration-500 ${usosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <i className={`${u.icon} text-3xl text-primary mb-4 block`} aria-hidden="true"></i>
                  <h3 className="font-heading font-bold text-lg text-secondary dark:text-white mb-2">{u.titulo}</h3>
                  <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{u.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Venues de feira em SP */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-4xl text-secondary dark:text-white mb-3">
                PRINCIPAIS PAVILHÕES E CENTROS DE FEIRA EM SÃO PAULO
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                A STIB atende estandes nos maiores espaços de feiras e convenções da cidade.
                Clique no seu venue para ver os detalhes de cada espaço.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {venuesFeiraSP.map((v, i) => (
                <Link
                  key={i}
                  to={`/locais/${v.slug}`}
                  className="group inline-flex items-center gap-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-6 py-4 hover:border-primary hover:bg-white dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-map-pin-2-line text-xl text-primary" aria-hidden="true"></i>
                  <span>
                    <span className="block font-heading font-bold text-secondary dark:text-white group-hover:text-primary transition-colors">{v.nome}</span>
                    <span className="block font-body text-xs text-gray-500 dark:text-gray-400">{v.bairro}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-5xl text-secondary dark:text-white mb-4">
                PERGUNTAS FREQUENTES
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300">Sobre aluguel de fones sem fio para estande em feiras</p>
            </div>
            <div ref={faqRef} className="space-y-0 bg-white dark:bg-gray-900 rounded-2xl">
              {perguntas.map((p, i) => (
                <FaqItem key={i} question={p.q} answer={p.a} index={i} isVisible={faqVisible} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 bg-secondary dark:bg-gray-800">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-heading font-bold text-5xl md:text-6xl text-white mb-6">
              Pronto para o Seu Estande na Próxima Feira?
            </h2>
            <p className="font-body text-xl text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">
              Solicite um orçamento gratuito informando a metragem do estande e o pavilhão. Respondemos em até 24 horas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-3 bg-primary text-black px-10 py-5 rounded-full font-body font-bold text-lg uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl" aria-hidden="true"></i>
                Solicitar Orçamento Grátis
              </Link>
              <a
                href="tel:+5511917776227"
                className="inline-flex items-center justify-center gap-3 border-2 border-white text-white px-10 py-5 rounded-full font-body font-bold text-lg uppercase hover:bg-white hover:text-secondary transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" aria-hidden="true"></i>
                (11) 91777-6227
              </a>
            </div>
          </div>
        </section>
      <PacotesBox texto="Expositores que participam de várias feiras no ano podem fechar um pacote de eventos para o calendário inteiro, com valor previsível e economia em relação a contratar feira por feira." />
      </main>
      <Footer />
    </div>
  );
}
